import { useEffect, useState } from 'react';
import {
    getOfflineTimeStatus,
    type OfflineTimeStatus
} from '@src/config/offlineLimit';

// Offline lockout is day-granular, so a coarse re-evaluation cadence is enough
// to transition an idle terminal into lockout (or refresh the "X more days"
// countdown) without user interaction. A short interval would only add render
// churn for no visible benefit.
const REEVALUATION_INTERVAL_MS = 60_000;

function isSameStatus(a: OfflineTimeStatus, b: OfflineTimeStatus): boolean {
    return (
        a.isLockedOut === b.isLockedOut &&
        a.isApproaching === b.isApproaching &&
        a.remainingDays === b.remainingDays
    );
}

/**
 * Reactively derives the offline time-limit status from `offlineSince`,
 * re-evaluating on an interval so the punch-pad banner and lockout screen stay
 * accurate as time passes — not only when React happens to re-render.
 */
export function useOfflineTimeStatus(
    offlineSince: Date | null
): OfflineTimeStatus {
    const [status, setStatus] = useState<OfflineTimeStatus>(() =>
        getOfflineTimeStatus(offlineSince)
    );

    useEffect(() => {
        function reevaluate() {
            setStatus(prev => {
                const next = getOfflineTimeStatus(offlineSince);
                return isSameStatus(prev, next) ? prev : next;
            });
        }

        reevaluate();
        if (!offlineSince) return;

        const id = setInterval(reevaluate, REEVALUATION_INTERVAL_MS);
        return () => clearInterval(id);
    }, [offlineSince]);

    return status;
}
